// === Translate Scene ===
// Client for POST /api/workflow/translate-scene on aidea-middleware. The
// middleware's TranslateSceneNode takes the scene's items, asks Azure GPT-4o
// to translate every learner-facing string, and returns one entry per item
// shaped for direct UPDATE_ITEM_PARAM dispatch.
//
// Authoritative response shape:
//   { translatedItems: [{ itemId, params }] }
//
// Errors surface as a thrown Error — the caller (translateSceneThunk) catches
// and emits a window-level toast event so the host shell can render a
// snackbar without coupling the studio to a specific notify module.
import { apiPost } from "../../common/http_service";
import { getToken } from "../../common/environment_config";

function authHeaders() {
  const token = getToken();
  const h = { Accept: "application/json" };
  if (token) h.Authorization = "Bearer " + token;
  if (typeof window !== "undefined" && window.tenantId) {
    h["Abp.TenantId"] = window.tenantId;
  }
  return h;
}

function translateSceneUrl() {
  const base = (typeof window !== "undefined" && window.ideaLibraryRootUrl) || "";
  return `${base.replace(/\/$/, "")}/api/workflow/translate-scene`;
}

/**
 * Translate every interactive item in a scene to a target locale.
 *
 * @param {object} body
 * @param {string|number} body.sectionId
 * @param {string|number} body.sceneId
 * @param {string} body.targetLocale         e.g. "tr", "es", "en"
 * @param {Array<{itemId:string, kind:string, params:object}>} body.items
 * @returns {Promise<Array<{itemId:string, params:object}>>}
 */
export async function translateScene({ sectionId, sceneId, targetLocale, items }) {
  if (sectionId === null || sectionId === undefined) {
    throw new Error("translateScene: sectionId is required");
  }
  if (sceneId === null || sceneId === undefined) {
    throw new Error("translateScene: sceneId is required");
  }
  if (!targetLocale) {
    throw new Error("translateScene: targetLocale is required");
  }
  const payload = {
    sectionId: String(sectionId),
    sceneId: String(sceneId),
    targetLocale: String(targetLocale),
    items: Array.isArray(items) ? items : [],
  };
  const response = await apiPost(translateSceneUrl(), payload, { headers: authHeaders() });
  if (!response || !response.ok) {
    const status = response && response.status;
    throw new Error(`translateScene request failed (status=${status})`);
  }
  const text = await response.text();
  let parsed;
  try {
    parsed = text ? JSON.parse(text) : {};
  } catch (e) {
    throw new Error(`translateScene response was not JSON: ${e.message}`);
  }
  const list = Array.isArray(parsed && parsed.translatedItems) ? parsed.translatedItems : [];
  return list;
}
// === /Translate Scene ===
